// RUM イベントデータから個人情報・機微情報を除去するモジュール。
// 方針: 「収集項目最小化（主防衛）+ 追加マスキング（補完）」の2層構造。
//
// 【第1層: 収集最小化 ← 主防衛ライン】
//   - context オブジェクトは CUSTOM_ATTRIBUTE_ALLOWLIST に含まれるキーのみ送信する
//   - 許可リストにないカスタム属性はそもそも Datadog へ送られない
//
// 【第2層: 追加マスキング ← 補完。第1層で除去しきれない場合のフォールバック】
//   - URL フィールドはクエリ文字列を削除する
//   - 文字列値はメール・電話番号・LINE ID・郵便番号をマスクする

// 【主防衛ライン】addAction に渡す context で送信を許可するキーの許可リスト。
// ここに含まれないカスタム属性は Datadog へ一切送信されない（収集最小化）
const CUSTOM_ATTRIBUTE_ALLOWLIST = new Set([
  "service",        // どのサービスか（例: "syounika", "obstetrics"）
  "env",            // 実行環境（例: "production", "staging"）
  "screen_group",   // 画面の大分類（例: "reservation", "consultation"）
  "screen_name",    // 画面の小分類・具体的な画面名（例: "reservation_confirm"）
  "event_version",  // イベント定義のスキーマバージョン（例: "1"）
  "entrypoint",     // ユーザーの流入経路（例: "lp", "top"）
  "auth_mode",      // 認証方式（例: "email", "line", "openid"）
  "flow_step",      // 複数ステップのフローにおける現在のステップ（例: "step_1", "confirm"）
]);

// 個人情報パターン: メールアドレス
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
// 個人情報パターン: 日本の電話番号（固定・携帯・国番号付き）
// NOTE: 先頭は `\b` ではなく直前1文字をキャプチャグループで判定する。`+` は非単語文字のため、
//   "tel:+819000..." のように直前が記号（`:` 等）の場合 \b が成立せず
//   マッチを取りこぼす（false negative）ため。
//   negative lookbehind（`(?<!\d)`）は古い Safari 等では正規表現リテラルの構文エラーとなり
//   このモジュール自体が読み込めなくなる（アプリの JS 実行が止まる）ため使用しない。
const PHONE_PATTERN = /(^|[^\d])((?:\+81|0)\d{1,4}[-\s]?\d{1,4}[-\s]?\d{3,4})\b/g;
// 個人情報パターン: LINE ID（@から始まる4文字以上）
const LINE_ID_PATTERN = /@[a-z0-9_]{4,}/gi;
// 個人情報パターン: 日本の郵便番号（〒あり・なし両対応）
const POSTAL_CODE_PATTERN = /〒?\d{3}-\d{4}/g;

// マスク後の置換値
const REDACTED_VALUE = "[REDACTED]";

// url / *_url という名前のキーを URL 系フィールドと判定する
const isUrlLikeKey = (key) => {
  if (typeof key !== "string") {
    return false;
  }

  return key === "url" || key.endsWith("_url");
};

// 文字列値から個人情報パターンをマスクする
const sanitizeString = (value) => {
  return value
    .replace(EMAIL_PATTERN, REDACTED_VALUE)
    .replace(PHONE_PATTERN, (_match, precedingChar) => `${precedingChar}${REDACTED_VALUE}`)
    .replace(LINE_ID_PATTERN, REDACTED_VALUE)
    .replace(POSTAL_CODE_PATTERN, REDACTED_VALUE);
};

// URL からクエリ文字列を除去する（個人情報がクエリに含まれる場合の保護）
const stripQueryString = (url) => {
  const [head] = url.split("?");
  return head;
};

const sanitizeValue = (value, key = null) => {
  if (typeof value === "string") {
    if (isUrlLikeKey(key)) {
      return stripQueryString(value);
    }

    return sanitizeString(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item, key));
  }

  if (value && typeof value === "object") {
    return sanitizeObject(value, false);
  }

  return value;
};

// オブジェクトの各キーをサニタイズする。
// enforceAllowlist が true の場合、許可リスト外のキーを除去する（context 向け）
const sanitizeObject = (target, enforceAllowlist) => {
  const sanitized = {};

  Object.entries(target).forEach(([key, value]) => {
    // context の場合は許可リスト外のキーを送信しない
    if (enforceAllowlist && !CUSTOM_ATTRIBUTE_ALLOWLIST.has(key)) {
      return;
    }

    if (typeof value === "string") {
      sanitized[key] = sanitizeValue(value, key);
      return;
    }

    if (value && typeof value === "object") {
      sanitized[key] = sanitizeValue(value, key);
      return;
    }

    sanitized[key] = value;
  });

  return sanitized;
};

// Datadog RUM SDK の beforeSend フック向けサニタイザー。
// 全イベント型（view/resource/error/action）に対して URL クエリ除去と個人情報マスクを適用する。
// context フィールドは許可リスト制御（収集最小化）を別途適用する。
// 戻り値 true でイベント送信を継続、false で破棄（現在は常に送信継続）
export const sanitizeRumEvent = (event) => {
  const mutableEvent = event;

  Object.entries(mutableEvent).forEach(([key, value]) => {
    // context は後で許可リストを使った専用処理を行うのでここではスキップ
    if (key === "context") {
      return;
    }

    mutableEvent[key] = sanitizeValue(value, key);
  });

  // view イベントの URL からクエリを除去する
  const view = mutableEvent.view;
  if (view && typeof view.url === "string") {
    view.url = stripQueryString(view.url);
  }

  // resource イベントの URL からクエリを除去する（resource等も網羅）
  const resource = mutableEvent.resource;
  if (resource && typeof resource.url === "string") {
    resource.url = stripQueryString(resource.url);
  }

  // トップレベルの url フィールドからクエリを除去する
  if (typeof mutableEvent.url === "string") {
    mutableEvent.url = stripQueryString(mutableEvent.url);
  }

  // context は許可リスト制御で不要なカスタム属性を除去する
  const context = mutableEvent.context;
  if (context && typeof context === "object") {
    mutableEvent.context = sanitizeObject(context, true);
  }

  return true;
};

// addAction に渡す context オブジェクトを許可リスト + マスキングでサニタイズする
export const sanitizeRumContext = (context) => {
  return sanitizeObject(context, true);
};
